/**
 * Portal Jobs API client wrappers.
 *
 * Endpoints (mounted at `/api/portal` in `routes/portalRoutes.js`):
 *   POST /api/portal/jobs              — enqueue a new job
 *   GET  /api/portal/jobs              — list/filter jobs
 *   GET  /api/portal/jobs/:id          — fetch a single job
 *   POST /api/portal/jobs/:id/cancel   — request cancellation
 *   GET  /api/portal/events/stream     — SSE multiplex (?channel=...)
 *   GET  /api/portal/jobs/stream       — SSE shortcut for ?channel=job-update
 *
 * Auth: bearer token via the shared axios interceptor for HTTP calls.
 * For SSE the token is appended as `?token=` because EventSource cannot
 * set request headers — same pattern as the watcher stream.
 */

import { apiClient } from '@lib/api/client';
import { tokenStorage } from '@lib/auth/storage';
import type {
  CreateJobInput,
  JobListParams,
  JobListResult,
  PortalJob,
  SseChannel,
} from './types';

/** Build query string for `list()` — flattens the optional `status` array. */
function buildListQuery(params: JobListParams): URLSearchParams {
  const qs = new URLSearchParams();
  if (params.status) {
    if (Array.isArray(params.status)) {
      params.status.forEach((s) => qs.append('status', s));
    } else {
      qs.set('status', params.status);
    }
  }
  if (params.kind) qs.set('kind', params.kind);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.deviceSerial) qs.set('deviceSerial', params.deviceSerial);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.page != null) qs.set('page', String(params.page));
  return qs;
}

export const portalJobsApi = {
  /**
   * Enqueue a new portal job.
   * Pass `idempotencyKey` to dedupe retries — the server returns the existing
   * job (200 OK) instead of creating a duplicate when the key matches.
   */
  async create(input: CreateJobInput): Promise<PortalJob> {
    const headers: Record<string, string> = {};
    if (input.idempotencyKey) {
      headers['Idempotency-Key'] = input.idempotencyKey;
    }
    const { data } = await apiClient.post<PortalJob>('/api/portal/jobs', input, { headers });
    return data;
  },

  async list(params: JobListParams = {}): Promise<JobListResult> {
    const qs = buildListQuery(params).toString();
    const url = qs ? `/api/portal/jobs?${qs}` : '/api/portal/jobs';
    const { data } = await apiClient.get<JobListResult>(url);
    return data;
  },

  async get(id: string): Promise<PortalJob> {
    const { data } = await apiClient.get<PortalJob>(`/api/portal/jobs/${id}`);
    return data;
  },

  /** Request cancellation. Server may still finish in-flight device commands. */
  async cancel(id: string): Promise<PortalJob> {
    const { data } = await apiClient.post<PortalJob>(`/api/portal/jobs/${id}/cancel`);
    return data;
  },

  /**
   * Build the SSE stream URL for a given channel.
   *
   * Returns `null` when there is no access token in storage — callers should
   * pass the result straight to `useEventSource` which treats `null` as
   * "do not connect". Token is included as a query param because EventSource
   * cannot send custom headers.
   */
  streamUrl(channel: SseChannel = 'job-update'): string | null {
    const token = tokenStorage.getAccessToken();
    if (!token) return null;
    return `/api/portal/events/stream?channel=${encodeURIComponent(
      channel,
    )}&token=${encodeURIComponent(token)}`;
  },
};

export type PortalJobsApi = typeof portalJobsApi;
